import { Link } from "react-router-dom";
import {
  Wand2, Video, Sparkles, Shield,
  Layers, Palette, Youtube, Zap, ArrowRight,
} from "lucide-react";
import { SEOHead, softwareAppJsonLd } from "@/components/seo/SEOHead.tsx";

const T = {
  ink: "#0a0a0a", inkSoft: "#6b6b6b", rule: "#e9e6df",
  paper: "#faf8f3", paper2: "#f3efe5", accent: "#ff5b1f",
};

const FEATURES = [
  {
    id: "script-generation",
    icon: Wand2,
    title: "AI Script Generation",
    headline: "Scripts that sound human, written in seconds",
    description:
      "Tell TubeForge your topic and tone. Our AI drafts a structured script — hook, body sections, and call-to-action — in under 30 seconds. Edit every word inline, or regenerate with a different tone (punchier, shorter, more casual). You stay in control; the AI does the heavy lifting.",
    bullets: [
      "Structured output: hook, body paragraphs, CTA",
      "5 tone presets: punchier, longer, shorter, casual, serious",
      "Per-section regeneration — keep what works, redo what doesn't",
      "Word count and estimated duration shown live",
    ],
  },
  {
    id: "storyboard",
    icon: Layers,
    title: "Visual Storyboard",
    headline: "Every scene illustrated before you commit",
    description:
      "Once you approve the script, TubeForge generates an image for every scene. Review frames in a grid, click any to edit its prompt, and regenerate until it matches your vision. Bulk-select and regenerate multiple frames at once. Nothing renders until you give the green light.",
    bullets: [
      "One image per script segment — full visual preview",
      "Edit prompts per frame with instant regeneration",
      "Bulk select and regenerate multiple frames",
      "Supports 16:9 (long-form) and 9:16 (Shorts)",
    ],
  },
  {
    id: "rendering",
    icon: Video,
    title: "Automated Rendering",
    headline: "Voiceover, captions, music — assembled automatically",
    description:
      "Hit approve on the storyboard and TubeForge handles the rest. AI voiceover is synced to each scene, captions are aligned word-by-word, and background music is mixed in. Watch the render progress in real-time with stage-by-stage updates via server-sent events.",
    bullets: [
      "AI voiceover with OpenAI or ElevenLabs voices",
      "Word-level caption alignment via Whisper",
      "Background music auto-mixed to voice levels",
      "Live render progress with ETA",
    ],
  },
  {
    id: "publishing",
    icon: Youtube,
    title: "One-Click Publishing",
    headline: "Publish straight to YouTube from your dashboard",
    description:
      "Connect your YouTube channel via OAuth and publish directly — title, description, tags, and AI-disclosure label are set from a single modal. Schedule uploads for later or publish immediately. Download the MP4 any time.",
    bullets: [
      "Direct YouTube upload with OAuth",
      "Title, description, tags, category in one step",
      "Scheduled publishing (Pro & Agency)",
      "MP4 download on every plan",
    ],
  },
  {
    id: "customization",
    icon: Palette,
    title: "Caption & Style Controls",
    headline: "Your brand, your look",
    description:
      "Choose caption fonts (default, bold, handwritten, mono), pick caption colors, and select from multiple thumbnail options per video. Channel presets save your niche settings so every new video starts with the right defaults.",
    bullets: [
      "4 caption font families",
      "Custom caption color picker",
      "Multiple thumbnail options per video",
      "Channel presets with saved niche and format",
    ],
  },
  {
    id: "quality",
    icon: Shield,
    title: "Quality Tiers",
    headline: "Better images and voices as you scale",
    description:
      "Free and Starter plans use Flux Schnell for fast image generation and OpenAI TTS. Pro upgrades to Flux Dev and ElevenLabs voices. Agency unlocks Flux Pro and ElevenLabs Multilingual — the highest fidelity available.",
    bullets: [
      "Flux Schnell → Dev → Pro image models",
      "OpenAI → ElevenLabs → Multilingual voice tiers",
      "More regens per segment on higher plans",
    ],
  },
];

export function FeaturesPage() {
  return (
    <>
      <SEOHead
        title="AI Video Creation Features"
        description="AI script generation, visual storyboards, automated rendering, one-click YouTube publishing, and caption customisation — all for faceless video creation."
        path="/features"
        jsonLd={softwareAppJsonLd()}
      />

      {/* Hero */}
      <section className="py-16 md:py-24 text-center" style={{ backgroundColor: T.paper }}>
        <div className="mx-auto max-w-3xl px-5 md:px-8">
          <p className="text-[13px] font-semibold uppercase tracking-widest mb-4" style={{ color: T.accent }}>
            Features
          </p>
          <h1
            className="text-3xl md:text-5xl font-bold leading-tight mb-5"
            style={{ fontFamily: '"Bricolage Grotesque", "Inter", system-ui', letterSpacing: "-0.03em" }}
          >
            AI-powered faceless video creation, <span style={{ color: T.accent }}>end to end</span>
          </h1>
          <p className="text-[16px] md:text-[18px] leading-relaxed max-w-xl mx-auto" style={{ color: T.inkSoft }}>
            TubeForge handles script writing, image generation, voiceover, captions, and rendering.
            You review and approve each step.
          </p>
        </div>
      </section>

      {/* Feature sections */}
      {FEATURES.map((feat, i) => {
        const Icon = feat.icon;
        const reversed = i % 2 === 1;
        return (
          <section
            key={feat.id}
            id={feat.id}
            className="py-16 md:py-20"
            style={{ backgroundColor: i % 2 === 0 ? T.paper : T.paper2 }}
          >
            <div className="mx-auto max-w-6xl px-5 md:px-8">
              <div className={`flex flex-col ${reversed ? "md:flex-row-reverse" : "md:flex-row"} items-start gap-12 md:gap-16`}>
                {/* Icon card */}
                <div className="flex-shrink-0 w-full md:w-80">
                  <div
                    className="rounded-2xl p-8 flex flex-col items-center text-center"
                    style={{
                      backgroundColor: "rgba(255,91,31,0.06)",
                      border: `1px solid ${T.rule}`,
                    }}
                  >
                    <div
                      className="w-14 h-14 rounded-xl flex items-center justify-center mb-4"
                      style={{ backgroundColor: T.accent }}
                    >
                      <Icon className="w-7 h-7 text-white" />
                    </div>
                    <h3 className="text-[15px] font-bold mb-1" style={{ color: T.ink }}>
                      {feat.title}
                    </h3>
                  </div>
                </div>

                {/* Text */}
                <div className="flex-1">
                  <h2
                    className="text-2xl md:text-3xl font-bold mb-4"
                    style={{
                      fontFamily: '"Bricolage Grotesque", "Inter", system-ui',
                      letterSpacing: "-0.02em",
                    }}
                  >
                    {feat.headline}
                  </h2>
                  <p className="text-[15px] leading-relaxed mb-6" style={{ color: T.inkSoft }}>
                    {feat.description}
                  </p>
                  <ul className="space-y-3">
                    {feat.bullets.map((b) => (
                      <li key={b} className="flex items-start gap-3 text-[14px]" style={{ color: T.ink }}>
                        <Sparkles className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: T.accent }} />
                        {b}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </section>
        );
      })}

      {/* CTA */}
      <section className="py-16 md:py-20 text-center" style={{ backgroundColor: T.paper }}>
        <div className="mx-auto max-w-2xl px-5">
          <h2
            className="text-2xl md:text-3xl font-bold mb-4"
            style={{ fontFamily: '"Bricolage Grotesque", "Inter", system-ui', letterSpacing: "-0.02em" }}
          >
            Ready to try it?
          </h2>
          <p className="text-[15px] mb-8" style={{ color: T.inkSoft }}>
            Create 2 free videos — no credit card required.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to="/signup"
              className="inline-flex items-center justify-center gap-2 text-[15px] font-semibold px-7 py-3.5 rounded-xl transition-opacity hover:opacity-90"
              style={{ backgroundColor: T.accent, color: "#fff" }}
            >
              <Zap className="w-4 h-4" /> Start free trial
            </Link>
            <Link
              to="/pricing"
              className="inline-flex items-center justify-center gap-2 text-[15px] font-medium px-7 py-3.5 rounded-xl border transition-colors hover:opacity-80"
              style={{ borderColor: T.rule, color: T.ink }}
            >
              View pricing <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
